import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Card, CardBody } from '../../ui/Card';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchFactoryClients, FactoryClientData } from '../../../pages/manage_clients/factory_clients/service/factoryClientService';
import { fetchIndividualClients, IndividualClientData } from '../../../pages/manage_clients/individual_clients/service/individualClientService';
import { fetchBankClients, BankClientData } from '../../../pages/manage_clients/bank_clients/service/bankClientService';
import { FiBriefcase, FiUsers, FiHome, FiMapPin, FiGlobe } from 'react-icons/fi';

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${theme.spacing[6]} ${theme.spacing[4]} !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid ${theme.colors.light.border.light};
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03) !important;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
    border-color: ${theme.colors.primary[200]};
  }
`;

const MetricIcon = styled.div<{ color: string }>`
  width: 64px;
  height: 64px;
  border-radius: ${theme.borderRadius.xl};
  background: linear-gradient(135deg, ${({ color }) => color}20, ${({ color }) => color}10);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing[4]};
  transition: transform 0.3s ease;

  ${MetricCard}:hover & {
    transform: scale(1.1);
  }

  svg {
    color: ${({ color }) => color};
    font-size: 28px;
  }
`;

const MetricValue = styled.div`
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[2]};
  line-height: 1;
  letter-spacing: -0.03em;
`;

const MetricLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  font-weight: ${theme.typography.fontWeight.medium};
  line-height: 1.4;
`;

interface Metric {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: string;
}

const ClientsAnalytics: React.FC = () => {
  const { companyData, user } = useAuth();
  const [factoryClients, setFactoryClients] = useState<FactoryClientData[]>([]);
  const [individualClients, setIndividualClients] = useState<IndividualClientData[]>([]);
  const [bankClients, setBankClients] = useState<BankClientData[]>([]);
  const [loading, setLoading] = useState(true);

  const companyId = companyData?.id || user?.uid || '';

  useEffect(() => {
    const fetchClientsData = async () => {
      try {
        const [factory, individual, bank] = await Promise.all([
          fetchFactoryClients(companyId),
          fetchIndividualClients(companyId),
          fetchBankClients(companyId),
        ]);
        setFactoryClients(factory);
        setIndividualClients(individual);
        setBankClients(bank);
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClientsData();
  }, [companyId]);

  // Calculate metrics
  const totalFactoryClients = factoryClients.length;
  const totalIndividualClients = individualClients.length;
  const totalBankBranches = bankClients.length;
  const totalClients = totalFactoryClients + totalIndividualClients + totalBankBranches;
  const uniqueBanks = new Set(bankClients.map((c) => c.bankName)).size;
  const uniqueIndustries = new Set(factoryClients.map((c) => c.industryName)).size;

  const metrics: Metric[] = [
    {
      icon: <FiBriefcase />,
      label: 'Total Clients',
      value: loading ? '...' : totalClients.toString(),
      color: theme.colors.primary[500],
    },
    {
      icon: <FiHome />,
      label: 'Factory Clients',
      value: loading ? '...' : totalFactoryClients.toString(),
      color: theme.colors.primary[600],
    },
    {
      icon: <FiUsers />,
      label: 'Individual Clients',
      value: loading ? '...' : totalIndividualClients.toString(),
      color: theme.colors.primary[500],
    },
    {
      icon: <FiBriefcase />,
      label: 'Bank Branches',
      value: loading ? '...' : totalBankBranches.toString(),
      color: theme.colors.primary[600],
    },
    {
      icon: <FiGlobe />,
      label: 'Unique Banks',
      value: loading ? '...' : uniqueBanks.toString(),
      color: theme.colors.accent.info,
    },
    {
      icon: <FiMapPin />,
      label: 'Industries',
      value: loading ? '...' : uniqueIndustries.toString(),
      color: theme.colors.accent.success,
    },
  ];

  return (
    <MetricsGrid>
      {metrics.map((metric, index) => (
        <MetricCard key={index}>
          <MetricIcon color={metric.color}>{metric.icon}</MetricIcon>
          <MetricValue>{metric.value}</MetricValue>
          <MetricLabel>{metric.label}</MetricLabel>
        </MetricCard>
      ))}
    </MetricsGrid>
  );
};

export default ClientsAnalytics;

